import apiClient from './api';

const BASE_URL = '/notifications';

export interface NotificationConfig {
  id: number;
  eventType: string;
  notificationType: string;
  enabled: boolean;
  recipients?: string;
  templateSubject?: string;
  templateBody?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateNotificationConfigDTO {
  eventType: string;
  notificationType: string;
  enabled?: boolean;
  recipients?: string;
  templateSubject?: string;
  templateBody?: string;
}

export interface UpdateNotificationConfigDTO {
  enabled?: boolean;
  recipients?: string;
  templateSubject?: string;
  templateBody?: string;
}

export interface NotificationStatus {
  email: {
    configured: boolean;
    provider: string;
    status: string;
  };
  sms: {
    configured: boolean;
    provider: string;
    status: string;
  };
  configurations: {
    total: number;
    actives: number;
    inactives: number;
  };
  events: Record<string, {
    label: string;
    email: string;
    sms: string;
  }>;
}

export const EVENT_TYPE_LABELS: Record<string, string> = {
  creation: 'Creation intervention',
  affectation: 'Affectation equipe',
  demarrage: 'Demarrage intervention',
  terminaison: 'Terminaison intervention',
  validation: 'Validation GID',
  annulation: 'Annulation intervention',
  urgence: 'Urgence / Critique',
};

export const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  email: 'Email',
  sms: 'SMS',
};

/**
 * Service API pour les notifications
 */
export const notificationService = {
  /**
   * Liste des configurations
   */
  async listConfigs(): Promise<NotificationConfig[]> {
    const response = await apiClient.get<NotificationConfig[]>(`${BASE_URL}/config`);
    return response.data;
  },

  /**
   * Detail d'une configuration
   */
  async getConfig(id: number): Promise<NotificationConfig> {
    const response = await apiClient.get<NotificationConfig>(`${BASE_URL}/config/${id}`);
    return response.data;
  },

  /**
   * Creation d'une configuration
   */
  async createConfig(data: CreateNotificationConfigDTO): Promise<NotificationConfig> {
    const response = await apiClient.post<NotificationConfig>(`${BASE_URL}/config`, {
      event_type: data.eventType,
      notification_type: data.notificationType,
      enabled: data.enabled,
      recipients: data.recipients,
      template_subject: data.templateSubject,
      template_body: data.templateBody,
    });
    return response.data;
  },

  /**
   * Modification d'une configuration
   */
  async updateConfig(id: number, data: UpdateNotificationConfigDTO): Promise<NotificationConfig> {
    const response = await apiClient.put<NotificationConfig>(`${BASE_URL}/config/${id}`, {
      enabled: data.enabled,
      recipients: data.recipients,
      template_subject: data.templateSubject,
      template_body: data.templateBody,
    });
    return response.data;
  },

  /**
   * Active/Desactive une configuration
   */
  async toggleConfig(id: number): Promise<{ success: boolean; enabled: boolean; message: string }> {
    const response = await apiClient.patch<{ success: boolean; enabled: boolean; message: string }>(
      `${BASE_URL}/config/${id}/toggle`
    );
    return response.data;
  },

  /**
   * Suppression d'une configuration
   */
  async deleteConfig(id: number): Promise<void> {
    await apiClient.delete(`${BASE_URL}/config/${id}`);
  },

  /**
   * Types d'evenements disponibles
   */
  async getEventTypes(): Promise<Record<string, string>> {
    const response = await apiClient.get<Record<string, string>>(`${BASE_URL}/event-types`);
    return response.data;
  },

  /**
   * Types de notification disponibles
   */
  async getNotificationTypes(): Promise<Record<string, string>> {
    const response = await apiClient.get<Record<string, string>>(`${BASE_URL}/notification-types`);
    return response.data;
  },

  /**
   * Envoyer une notification de test
   */
  async sendTest(type: string, recipient: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post<{ success: boolean; message: string }>(
      `${BASE_URL}/test`,
      { type, recipient }
    );
    return response.data;
  },

  /**
   * Statut du systeme de notification
   */
  async getStatus(): Promise<NotificationStatus> {
    const response = await apiClient.get<NotificationStatus>(`${BASE_URL}/status`);
    return response.data;
  },

  /**
   * Initialiser les configurations par defaut
   */
  async initConfigs(): Promise<{ success: boolean; message: string; created: number }> {
    const response = await apiClient.post<{ success: boolean; message: string; created: number }>(
      `${BASE_URL}/config/init`
    );
    return response.data;
  },
};

export default notificationService;
